import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  FormLayout,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { getMe } from "../api/me";
import {
  createTaxRate,
  deleteTaxRate,
  listTaxRates,
  updateTaxRate,
  type ShopTaxRate,
} from "../api/tax_rates";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { percentToRate, rateToPercent } from "../utils/taxRatePercent";

import { t } from "../lib/i18n";

/**
 * 仕入税率（`shop_tax_rates`）の編集画面＝国別税モデルの要石。
 *
 * 国プリセット（Step 4）は初期値の種にすぎず、一度種付けした店は再種付けしない＝
 * 誤った国で種付けされた店・国があとから変わった店・CA（プリセット行なし）の
 * 是正手段はこの画面（これが入るまでは運用 SQL のみだった）。
 *
 * 🔴 税率0行は正当な状態（US 等＝税セクション非表示・v168）＝空でも壊れない。
 */
export function TaxRateSettings() {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const ratesQuery = useQuery({ queryKey: ["tax-rates"], queryFn: listTaxRates });

  // 追加フォーム
  const [newName, setNewName] = useState("");
  const [newPercent, setNewPercent] = useState("");
  // 行のインライン編集（同時に1行だけ）
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editPercent, setEditPercent] = useState("");

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["tax-rates"] });
    // 税区分セレクトの選択肢・税集計はドラフト応答（backend 配信）から作られる。
    queryClient.invalidateQueries({ queryKey: ["purchase-order-drafts"] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      createTaxRate({
        name: newName.trim(),
        // canAdd が percentToRate の成功を保証している（下の disabled 参照）
        rate: percentToRate(newPercent) ?? "0.0000",
      }),
    onSuccess: () => {
      setNewName("");
      setNewPercent("");
      invalidate();
    },
  });
  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; name: string; rate: string }) =>
      updateTaxRate(vars.id, { name: vars.name, rate: vars.rate }),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
  });
  const defaultMutation = useMutation({
    mutationFn: (id: string) => updateTaxRate(id, { is_default: true }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTaxRate(id),
    onSuccess: invalidate,
  });

  const rates = ratesQuery.data?.tax_rates ?? [];
  const isCanadaStore = (meQuery.data?.country_code ?? "").trim().toUpperCase() === "CA";

  const percentError = (value: string): string | undefined =>
    value.trim() !== "" && percentToRate(value) === null
      ? t("税率は 0〜100 のパーセント（小数第2位まで）で入力してください")
      : undefined;

  const canAdd =
    newName.trim() !== "" && percentToRate(newPercent) !== null;
  const canSaveEdit =
    editName.trim() !== "" && percentToRate(editPercent) !== null;

  const startEditing = (rate: ShopTaxRate) => {
    setEditingId(rate.id);
    setEditName(rate.name);
    setEditPercent(rateToPercent(rate.rate));
  };

  const handleDelete = (rate: ShopTaxRate) => {
    if (
      window.confirm(
        t("税率「{name}」を削除します。この税率を使っている商品は、次の発注書から既定の税率で計算されます。", {
          name: rate.name,
        }),
      )
    ) {
      deleteMutation.mutate(rate.id);
    }
  };

  const mutationError =
    createMutation.error ??
    updateMutation.error ??
    defaultMutation.error ??
    deleteMutation.error;

  return (
    <Page
      title={t("仕入税率")}
      subtitle={t("発注書の税区分に使う税率です（名前と率はこの店専用の設定）")}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {isCanadaStore && (
              <Banner tone="info">
                {t("カナダのストアには税率のプリセットがありません。州により GST に PST・HST・QST などが加わり税率が異なるためです。適用される州の税率を、ここで行として追加できます。")}
              </Banner>
            )}
            {mutationError && (
              <Banner tone="critical">
                {t("保存に失敗しました:")} {mutationError.message}
              </Banner>
            )}
            {ratesQuery.isLoading ? (
              <Card>
                <SkeletonBodyText lines={4} />
              </Card>
            ) : ratesQuery.isError ? (
              <QueryErrorBanner
                title={t("税率の取得に失敗しました")}
                onRetry={() => void ratesQuery.refetch()}
              />
            ) : (
              <Card>
                <BlockStack gap="400">
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {t("国のプリセットは初期値にすぎません。税率の変更・法改正への反映はこの画面で行ってください（自動では変わりません）。変更はこれから作る発注書から反映され、確定済みの発注書の金額は変わりません。")}
                  </Text>
                  {rates.length === 0 && (
                    <Banner tone="info">
                      {t("税率が1件も無いため、発注書に税のセクションは表示されません。仕入税を載せる場合は、ここで追加してください。")}
                    </Banner>
                  )}
                  <BlockStack gap="300">
                    {rates.map((rate) => (
                      <Box key={rate.id}>
                        {editingId === rate.id ? (
                          <BlockStack gap="200">
                            <FormLayout>
                              <FormLayout.Group>
                                <TextField
                                  label={t("名前")}
                                  value={editName}
                                  onChange={setEditName}
                                  autoComplete="off"
                                />
                                <TextField
                                  label={t("税率")}
                                  value={editPercent}
                                  onChange={setEditPercent}
                                  suffix="%"
                                  autoComplete="off"
                                  error={percentError(editPercent)}
                                />
                              </FormLayout.Group>
                            </FormLayout>
                            <InlineStack gap="200">
                              <Button
                                variant="primary"
                                loading={updateMutation.isPending}
                                disabled={!canSaveEdit}
                                onClick={() =>
                                  updateMutation.mutate({
                                    id: rate.id,
                                    name: editName.trim(),
                                    rate: percentToRate(editPercent) ?? rate.rate,
                                  })
                                }
                              >
                                {t("保存")}
                              </Button>
                              <Button onClick={() => setEditingId(null)}>
                                {t("取消")}
                              </Button>
                            </InlineStack>
                          </BlockStack>
                        ) : (
                          <InlineStack gap="300" blockAlign="center" wrap={false}>
                            <Box minWidth="0" width="100%">
                              <InlineStack gap="200" blockAlign="center">
                                <Text as="span" variant="bodyMd" fontWeight="semibold">
                                  {rate.name}
                                </Text>
                                <Text as="span" variant="bodyMd" tone="subdued">
                                  {rateToPercent(rate.rate)}%
                                </Text>
                                {rate.is_default && (
                                  <Badge tone="info">{t("既定")}</Badge>
                                )}
                              </InlineStack>
                            </Box>
                            <InlineStack gap="200" wrap={false}>
                              <Button
                                variant="plain"
                                onClick={() => startEditing(rate)}
                              >
                                {t("編集")}
                              </Button>
                              {!rate.is_default && (
                                <Button
                                  variant="plain"
                                  loading={defaultMutation.isPending}
                                  onClick={() => defaultMutation.mutate(rate.id)}
                                >
                                  {t("既定にする")}
                                </Button>
                              )}
                              <Button
                                variant="plain"
                                tone="critical"
                                loading={deleteMutation.isPending}
                                onClick={() => handleDelete(rate)}
                              >
                                {t("削除")}
                              </Button>
                            </InlineStack>
                          </InlineStack>
                        )}
                        <Box paddingBlockStart="300">
                          <Divider />
                        </Box>
                      </Box>
                    ))}
                  </BlockStack>
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      {t("税率を追加")}
                    </Text>
                    <FormLayout>
                      <FormLayout.Group>
                        <TextField
                          label={t("名前")}
                          value={newName}
                          onChange={setNewName}
                          placeholder={t("例: 軽減 8%")}
                          autoComplete="off"
                        />
                        <TextField
                          label={t("税率")}
                          value={newPercent}
                          onChange={setNewPercent}
                          suffix="%"
                          autoComplete="off"
                          error={percentError(newPercent)}
                        />
                      </FormLayout.Group>
                    </FormLayout>
                    <InlineStack>
                      <Button
                        variant="primary"
                        loading={createMutation.isPending}
                        disabled={!canAdd}
                        onClick={() => createMutation.mutate()}
                      >
                        {t("追加")}
                      </Button>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t("0% の税率も作れます（免税・リバースチャージなど）。名前は発注書（画面・PDF・CSV）にそのまま表示されます。")}
                    </Text>
                  </BlockStack>
                </BlockStack>
              </Card>
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
